import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';

import { Modal, useT } from '@repo/common';
import EducationFullPage from './EducationFullPage';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/16px/icon_16px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/24px/icon_24px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';

const TIP_TEXT =
  'For Korean resumes, start with your high school and list each degree onward.';

const PLACEHOLDER_TEXT = 'Tap to add your school and degree';

const EMPTY_ENTRY = {
  schoolName: '',
  fieldOfStudy: '',
  degree: '',
  studyPeriod: '',
};

const formatTitle = (edu, t) => {
  const degree = edu.degree?.trim();
  const school = edu.schoolName?.trim();
  if (degree && school) {
    return t(`${degree} at ${school}`, `${school} · ${degree}`);
  }
  return school || degree;
};

const PlaceholderCard = ({ onClick }) => {
  const t = useT();

  return (
    <div className='flex items-center'>
      <span className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray04 py-[14px] w-[20px]'>
        <DragAndDropIcon />
      </span>
      <button
        type='button'
        onClick={onClick}
        className='flex-1 text-left rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 p-3 typo-b14Strong text-text-placeholder'
      >
        {t(PLACEHOLDER_TEXT, '탭하여 학교와 학위를 추가하세요')}
      </button>
    </div>
  );
};

const MobileEducationForm = ({ education, setResumeData }) => {
  const t = useT();
  const list = Array.isArray(education) ? education : [];

  const [editor, setEditor] = useState({ open: false, mode: 'add', id: null });
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const commit = (next) =>
    setResumeData((prev) => ({ ...prev, education: next }));

  const openAdd = () => setEditor({ open: true, mode: 'add', id: null });
  const openEdit = (id) => setEditor({ open: true, mode: 'edit', id });
  const closeEditor = () => setEditor({ open: false, mode: 'add', id: null });

  const addRow = () => commit([...list, { id: uuidv4(), ...EMPTY_ENTRY }]);

  const handleSave = (data) => {
    if (editor.mode === 'add') {
      commit([...list, { id: uuidv4(), ...data }]);
    } else {
      commit(list.map((e) => (e.id === editor.id ? { ...e, ...data } : e)));
    }
    closeEditor();
  };

  const handleConfirmDelete = () => {
    commit(list.filter((e) => e.id !== deleteTargetId));
    setDeleteTargetId(null);
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(list);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    commit(items);
  };

  const editingEntry =
    editor.mode === 'edit' ? list.find((e) => e.id === editor.id) : null;

  return (
    <div className='flex flex-col gap-3 w-full'>
      {list.length === 0 ? (
        <PlaceholderCard onClick={openAdd} />
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId='mobile-educations' direction='vertical'>
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='flex flex-col gap-3 w-full'
              >
                {list.map((edu, index) => {
                  const title = formatTitle(edu, t);

                  return (
                    <Draggable
                      key={edu.id}
                      draggableId={String(edu.id)}
                      index={index}
                    >
                      {(prov) => (
                        <div
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          className='flex items-center gap-2 w-full'
                        >
                          {/* Drag handle */}
                          <span
                            {...prov.dragHandleProps}
                            className='shrink-0 cursor-grab active:cursor-grabbing text-grayscale-gray03'
                          >
                            <DragAndDropIcon width={20} height={20} />
                          </span>

                          {/* 요약 버튼 */}
                          <button
                            type='button'
                            onClick={() => openEdit(edu.id)}
                            className='flex-1 min-w-0 w-full flex items-start gap-1 rounded-[8px] border border-grayscale-gray02 bg-grayscale-gray00 px-4 py-3'
                          >
                            <div className='flex flex-col items-start gap-1 w-full'>
                              <p
                                className={`truncate typo-b14Strong ${
                                  title
                                    ? 'text-text-primary'
                                    : 'text-text-placeholder'
                                }`}
                              >
                                {title ||
                                  t(
                                    PLACEHOLDER_TEXT,
                                    '탭하여 학교와 학위를 추가하세요',
                                  )}
                              </p>
                              {edu.fieldOfStudy?.trim() && (
                                <p className='truncate typo-b12 text-text-secondary'>
                                  {edu.fieldOfStudy}
                                </p>
                              )}
                              {edu.studyPeriod?.trim() && (
                                <p className='truncate typo-b12 text-text-secondary'>
                                  {edu.studyPeriod}
                                </p>
                              )}
                            </div>
                            {/* Delete */}
                            <button
                              type='button'
                              onClick={() => setDeleteTargetId(edu.id)}
                              className='shrink-0 text-grayscale-gray04 w-6 min-h-[26px] flex justify-end items-start'
                              aria-label={t('Delete', '삭제')}
                            >
                              <DeleteIcon width={20} height={20} />
                            </button>
                          </button>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <button
        type='button'
        onClick={addRow}
        className='flex items-center justify-center h-12 rounded-[8px] bg-grayscale-gray01 text-grayscale-gray05 transition-colors active:bg-grayscale-gray02'
        aria-label={t('Add education', '학력 추가하기')}
      >
        <PlusIcon width={20} height={20} />
      </button>

      {editor.open && (
        <EducationFullPage
          initialData={editingEntry}
          onClose={closeEditor}
          onSave={handleSave}
        />
      )}

      {deleteTargetId && (
        <Modal
          isOpen
          onClickLeftBtn={() => setDeleteTargetId(null)}
          onClickRightBtn={handleConfirmDelete}
          title={t(
            'Delete this education entry?',
            '이 학력 항목을 삭제할까요?',
          )}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 되돌릴 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default MobileEducationForm;
